<script setup lang="ts">
import { hasPermission } from '@permission/hasPermission';
import { useClaims } from '@auth/useClaims';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { useBerichtenProlongatiewijzigingenLinkNaarRelatieMailen } from '@services/communicatie-public/orval-api/berichten-api/berichten-api';
import { VButton } from '@shared/component/VButton';
import VCollapsibleSection from '@shared/component/VCollapsibleSection/VCollapsibleSection.vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import { VTooltip } from '@shared/component/VTooltip';
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useRouteParams } from '@vueuse/router';
import { computed, reactive } from 'vue';
import { useProlongatie } from '../../composables/prolongatie';
import { useProlongatieHistorie } from '../../composables/prolongatie-historie';
import { useRelatie } from '../../composables/relatie';
import VBerichten from './VBerichten.vue';
import VFilters from './VFilters.vue';
import VVoorwaarden from './VVoorwaarden.vue';
import { useTitle } from './title';

const polisnummer = useRouteParams('polisnummer', '');

const state = reactive<{
  filters: {
    jaar?: number;
    pakket?: string;
  };
}>({
  filters: {
    pakket: polisnummer.value || undefined,
  },
});

const claims = useClaims();
const notification = useNotification();

const getProlongatie = useProlongatie({
  staleTime: 60_000,
});

const getProlongatieHistorie = useProlongatieHistorie(
  () => state.filters.pakket,
  () => state.filters.jaar,
  {
    enabled: () => !!state.filters.pakket,
  },
);

const getRelatie = useRelatie({
  enabled: () => claims.value.isTemporaryUser === false,
  staleTime: 60_000,
});

const data = computed(() => getProlongatieHistorie.data.value ?? getProlongatie.data.value);

const isPending = computed(
  () =>
    (getProlongatieHistorie.isPending.value && getProlongatieHistorie.isEnabled.value) ||
    (getRelatie.isPending.value && getRelatie.isEnabled.value) ||
    getProlongatie.isPending.value,
);

const { title } = useTitle(isPending, () => state.filters.pakket);

const linkNaarRelatieVersturenPermission = hasPermission(
  'Communicatie.ProlongatiewijzigingenLinkNaarRelatieVersturen',
);
const relatienummer = useRouteParams('relatienummer', '', {
  transform: (value) => Number.parseInt(value, 10),
});

const { mutate, isPending: linkNaarRelatieMailenIsPending } =
  useBerichtenProlongatiewijzigingenLinkNaarRelatieMailen();

useWatchErrorHandler(getProlongatie.error);
useWatchErrorHandler(getProlongatieHistorie.error);
useWatchErrorHandler(getRelatie.error);

function linkNaarRelatieMailen() {
  mutate(
    { relatienummer: relatienummer.value },
    {
      onSuccess() {
        notification.success('De e-mail is verstuurd naar de relatie');
      },
    },
  );
}
</script>

<template>
  <VPageContainer container-size="sm" title="Prolongatiewijzigingen">
    <template #actions>
      <VTooltip label="Stuur de relatie een mail met een link naar deze pagina">
        <VButton
          v-if="linkNaarRelatieVersturenPermission"
          :loading="linkNaarRelatieMailenIsPending"
          inverted
          @click="linkNaarRelatieMailen"
          prepend-icon="far fa-envelope"
          confirm-dialog
          confirm-dialog-text="Weet je zeker dat je de relatie een mail wilt sturen met een link naar deze pagina?"
          confirm-button-text="Mail versturen"
        >
          Mail naar relatie
        </VButton>
      </VTooltip>
    </template>

    <VContentBox :margin-bottom="0" no-padding :title>
      <template #header-end>
        <VFilters
          v-show="!isPending"
          v-model:jaar="state.filters.jaar"
          v-model:pakket="state.filters.pakket"
        />
      </template>

      <VCollapsibleSection open>
        <template #title>Berichten</template>

        <template #content>
          <section class="px-3">
            <VBerichten :berichten="data?.berichten" :loading="isPending" />
          </section>
        </template>
      </VCollapsibleSection>

      <VCollapsibleSection open>
        <template #title>Voorwaarden</template>

        <template #content>
          <section class="p-3 pl-8">
            <VVoorwaarden :voorwaarden="data?.voorwaarden" :loading="isPending" />
          </section>
        </template>
      </VCollapsibleSection>
    </VContentBox>
  </VPageContainer>
</template>
